import type { PiiSpan, PiiType } from "./types";

/**
 * Coreference + numbering pass (LAR-27 v2, plan decisions 3 & 4).
 *
 * After detection produces a flat list of spans, this collapses mentions that
 * refer to the SAME entity onto one stable numbered placeholder, so a prompt
 * stays readable and its references survive redaction:
 *
 *   "Min vens navn er Peter Hansen. Peter kan godt lide chokolade.
 *    Peters båd holder ved Christianshavn"
 *   → "… [NAVN1]. [NAVN1] kan godt lide chokolade.
 *      [NAVN1]s båd holder ved [ADRESSE1]"
 *
 * Two independent mechanisms feed one per-type numbering counter:
 *  - Names are grouped by **name-variant clustering** (union-find over lowercased
 *    word tokens: subset containment, plus a bare first/last name folded into a
 *    full-name cluster only when EXACTLY ONE such cluster exists). We deliberately
 *    **bias to under-merge**: a wrong merge collapses two real people onto one
 *    token — a cross-person privacy leak — whereas a missed merge only costs a
 *    duplicate number. No pronoun resolution.
 *  - Everything else (hard IDs, addresses, orgs, custom) is keyed by its
 *    normalised value, so the same CPR in two formats is one `[CPR1]`.
 *
 * The Danish genitive is stripped for clustering and recorded as an `inflection`
 * so redaction can re-append it (`[NAVN1]s`). We use only the two safe rules —
 * a trailing apostrophe (names ending in s/x/z: `Anders'`), and a trailing `-s`
 * **but only when corroborated** by the bare name appearing elsewhere — never a
 * Snowball-style stemmer, which mangles real names.
 *
 * This module is pure and framework-free so the Python document worker can port
 * it verbatim; the shared fixtures assert the spec, not byte-parity.
 */

/** Curly and straight apostrophes both mark the Danish s/x/z genitive. */
const APOSTROPHES = new Set(["'", "’"]);

/** Split a name surface into lowercased word tokens (whitespace + hyphen). */
function nameTokens(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[\s-]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

/**
 * Normalised value for non-name types, so differently-formatted mentions of the
 * same identifier share a number. Names are handled by clustering, not this.
 */
function normalizeValue(span: PiiSpan): string {
  const t = span.text;
  switch (span.type) {
    case "cpr":
      return t.replace(/\D/g, ""); // digits only → 010190-1234 == 0101901234
    case "phone":
      return t.replace(/\D/g, "").slice(-8); // significant 8 digits
    case "email":
      return t.trim().toLowerCase();
    case "account":
      return t.replace(/\s/g, "").toUpperCase();
    default: // address, org, custom
      return t.trim().toLowerCase().replace(/\s+/g, " ");
  }
}

/** Minimal union-find over the local name-mention indices. */
class UnionFind {
  private parent: number[];
  constructor(size: number) {
    this.parent = Array.from({ length: size }, (_, i) => i);
  }
  find(x: number): number {
    while (this.parent[x] !== x) {
      this.parent[x] = this.parent[this.parent[x]];
      x = this.parent[x];
    }
    return x;
  }
  union(a: number, b: number): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent[Math.max(ra, rb)] = Math.min(ra, rb);
  }
}

/** True iff set `a` is a (non-strict) subset of set `b`. */
function isSubset(a: Set<string>, b: Set<string>): boolean {
  for (const x of a) if (!b.has(x)) return false;
  return true;
}

interface NameClustering {
  /** Global-span-index → canonical cluster key. */
  keyBySpanIndex: Map<number, string>;
  /** Global-span-index → genitive suffix to re-append after the placeholder. */
  inflectionBySpanIndex: Map<number, string | undefined>;
}

/**
 * Cluster the `name` spans (given by their indices into `spans`) into coreferent
 * groups and resolve each mention's genitive inflection.
 */
function clusterNames(spans: PiiSpan[], indices: number[]): NameClustering {
  const n = indices.length;
  const tokens: string[][] = [];
  const inflection: Array<string | undefined> = [];

  // 1. Tokenise; strip a trailing apostrophe genitive (the safe, unambiguous rule).
  for (const gi of indices) {
    const toks = nameTokens(spans[gi].text);
    let infl: string | undefined;
    if (toks.length > 0) {
      const last = toks[toks.length - 1];
      if (APOSTROPHES.has(last[last.length - 1])) {
        toks[toks.length - 1] = last.slice(0, -1);
        infl = "'";
      }
    }
    tokens.push(toks);
    inflection.push(infl);
  }

  // 2. Token presence across DISTINCT mentions, computed before any -s stripping;
  //    used purely as corroborating evidence for the genitive.
  const presence = new Map<string, number>();
  for (const toks of tokens) {
    for (const tk of new Set(toks)) presence.set(tk, (presence.get(tk) ?? 0) + 1);
  }

  // 3. Corroborated -s genitive: strip a trailing "s" ONLY when the bare token
  //    already appears elsewhere (so "Peters"→"Peter" folds, but a real name
  //    like "Anders" is left alone because "Ander" appears nowhere).
  tokens.forEach((toks, li) => {
    if (inflection[li] || toks.length === 0) return;
    const last = toks[toks.length - 1];
    if (last.length >= 4 && last.endsWith("s")) {
      const stripped = last.slice(0, -1);
      if ((presence.get(stripped) ?? 0) >= 1) {
        toks[toks.length - 1] = stripped;
        inflection[li] = "s";
      }
    }
  });

  // 4. Union-find clustering over the (post-strip) token sets.
  const sets = tokens.map((t) => new Set(t));
  const uf = new UnionFind(n);
  const isMulti = sets.map((s) => s.size >= 2);

  // 4a. Merge multi-token mentions by subset containment (conservative).
  for (let i = 0; i < n; i++) {
    if (!isMulti[i]) continue;
    for (let j = i + 1; j < n; j++) {
      if (!isMulti[j]) continue;
      if (isSubset(sets[i], sets[j]) || isSubset(sets[j], sets[i])) uf.union(i, j);
    }
  }

  // 4b. Fold a single-token mention into a full-name cluster ONLY if it belongs
  //     to exactly one — otherwise (0 or ≥2) keep it separate (under-merge).
  const attached = new Set<number>();
  for (let i = 0; i < n; i++) {
    if (isMulti[i] || sets[i].size === 0) continue;
    const [tok] = [...sets[i]];
    const roots = new Set<number>();
    for (let j = 0; j < n; j++) {
      if (isMulti[j] && sets[j].has(tok)) roots.add(uf.find(j));
    }
    if (roots.size === 1) {
      uf.union(i, [...roots][0]);
      attached.add(i);
    }
  }

  // 4c. Merge still-unattached single mentions that share the same token
  //     (identical surface → same reference under our under-merge posture).
  for (let i = 0; i < n; i++) {
    if (isMulti[i] || attached.has(i) || sets[i].size === 0) continue;
    for (let j = i + 1; j < n; j++) {
      if (isMulti[j] || attached.has(j) || sets[j].size === 0) continue;
      const [ti] = [...sets[i]];
      const [tj] = [...sets[j]];
      if (ti === tj) uf.union(i, j);
    }
  }

  // 5. Canonical key per cluster = the union of all member tokens, sorted. This is
  //    order-independent and distinct across non-merged clusters (differing surnames
  //    → differing unions), so two different "Peter"s never collide.
  const tokensByRoot = new Map<number, Set<string>>();
  for (let i = 0; i < n; i++) {
    const r = uf.find(i);
    const acc = tokensByRoot.get(r) ?? new Set<string>();
    for (const tk of sets[i]) acc.add(tk);
    tokensByRoot.set(r, acc);
  }

  const keyBySpanIndex = new Map<number, string>();
  const inflectionBySpanIndex = new Map<number, string | undefined>();
  for (let i = 0; i < n; i++) {
    const r = uf.find(i);
    const key = [...tokensByRoot.get(r)!].sort().join(" ");
    keyBySpanIndex.set(indices[i], `name:${key}`);
    inflectionBySpanIndex.set(indices[i], inflection[i]);
  }
  return { keyBySpanIndex, inflectionBySpanIndex };
}

/**
 * Assign `entityId` (and, for names, `inflection`) to every span.
 *
 * Numbering is **per type, in first-appearance order, reset per call** (i.e. per
 * message / per chunk): coreferent name mentions share a cluster key, other types
 * share their normalised value, and each distinct key gets the next number for
 * its type — `[NAVN1]`, `[NAVN2]`, `[CPR1]`, `[ADRESSE1]`. Returns a new array
 * sorted by position; inputs are not mutated.
 */
export function numberSpans(input: readonly PiiSpan[]): PiiSpan[] {
  const spans = [...input].sort((a, b) => a.start - b.start || a.end - b.end);

  const keyByIndex = new Array<string>(spans.length);
  const inflByIndex = new Array<string | undefined>(spans.length);
  const nameIndices: number[] = [];

  spans.forEach((s, i) => {
    if (s.type === "name") {
      nameIndices.push(i);
    } else {
      keyByIndex[i] = `${s.type}:${normalizeValue(s)}`;
      inflByIndex[i] = undefined;
    }
  });

  if (nameIndices.length > 0) {
    const { keyBySpanIndex, inflectionBySpanIndex } = clusterNames(
      spans,
      nameIndices
    );
    for (const i of nameIndices) {
      keyByIndex[i] = keyBySpanIndex.get(i)!;
      inflByIndex[i] = inflectionBySpanIndex.get(i);
    }
  }

  const counters = new Map<PiiType, Map<string, number>>();
  return spans.map((s, i) => {
    let m = counters.get(s.type);
    if (!m) {
      m = new Map<string, number>();
      counters.set(s.type, m);
    }
    const key = keyByIndex[i];
    if (!m.has(key)) m.set(key, m.size + 1);
    return { ...s, entityId: m.get(key), inflection: inflByIndex[i] };
  });
}
